import type {
  IPluginLoadTelemetryRepository,
  PluginLoadEvent,
} from '@/domain/repositories/IPluginLoadTelemetryRepository';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { API_BASE_URL } from '@/infrastructure/http/apiConfig';

function buildPluginLoadEventsUrl(): string {
  return `${API_BASE_URL}/plugin-load-events`;
}

function toRequestBody(event: PluginLoadEvent): Record<string, unknown> {
  if (event.success) {
    return {
      activation_type: event.activationType,
      success: true,
      load_duration_ms: event.loadDurationMs,
      retry_count: event.retryCount,
    };
  }
  return {
    activation_type: event.activationType,
    success: false,
    load_duration_ms: event.loadDurationMs,
    retry_count: event.retryCount,
    last_error_code: event.lastErrorCode,
    last_error_message: event.lastErrorMessage,
  };
}

/**
 * Implementação que registra telemetria de carregamento de plugin chamando
 * `POST /plugin-load-events` com `Authorization: Bearer <jwtToken>`
 * (HUB-237, CA-13/CA-14/CA-21).
 *
 * Propaga qualquer erro normalmente (nunca o engole aqui) — a política de
 * "melhor esforço, nunca bloqueante" (CA-21) é responsabilidade de
 * `RecordPluginLoadEventUseCase`, não desta implementação de infra.
 */
export class HttpPluginLoadTelemetryRepository implements IPluginLoadTelemetryRepository {
  async record(session: DeviceSession, event: PluginLoadEvent): Promise<void> {
    const response = await fetch(buildPluginLoadEventsUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.jwtToken}`,
      },
      body: JSON.stringify(toRequestBody(event)),
    });

    if (!response.ok) {
      throw new Error(`Falha ao registrar telemetria de carregamento: HTTP ${response.status}`);
    }
  }
}
